import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { TimeInput24h } from '../ui/time-input-24h';
import { 
  ArrowLeft, 
  Users, 
  User, 
  Car, 
  TrendingUp, 
  TrendingDown,
  Calendar,
  DollarSign,
  MapPin,
  Clock,
  Filter,
  FileText,
  Wallet,
  Plus,
  Trash2,
  Edit,
  X,
  Navigation,
  Circle,
  Map,
  Wrench,
  Save,
  BarChart3,
  Banknote,
  Gift,
  Calculator
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SalaryReport from './SalaryReport';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }
});

export default function EmployeeReports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('employees'); // 'employees' or 'salary'
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [trips, setTrips] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [mechanicTasks, setMechanicTasks] = useState([]);
  const [bonusesPenalties, setBonusesPenalties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const [showBonusDialog, setShowBonusDialog] = useState(false);
  const [editMechTask, setEditMechTask] = useState(null);
  const [showEditMechDialog, setShowEditMechDialog] = useState(false);
  const [bonusForm, setBonusForm] = useState({
    type: 'bonus',
    amount: '',
    reason: '',
    date: new Date().toISOString().split('T')[0]
  });
  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    return formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [dateTo, setDateTo] = useState(() => {
    const now = new Date();
    return formatDateLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  });
  const [periodPreset, setPeriodPreset] = useState('thisMonth');
  const [editingTrip, setEditingTrip] = useState(null);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchUserData();
    }
  }, [selectedUser, dateFrom, dateTo]);

  const fetchUsers = async () => {
    try {
      const [usersResponse, busesResponse, driversResponse] = await Promise.all([
        axios.get(`${API}/api/users`, getAuthHeaders()),
        axios.get(`${API}/api/buses?thumbnails_only=true`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/api/drivers`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      const employees = usersResponse.data.filter(u => 
        u.is_driver || u.role === 'driver' || u.is_mechanic
      );
      setUsers(employees);
      setBuses(busesResponse.data || []);
      setDrivers(driversResponse.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    if (!selectedUser) return;
    
    setDataLoading(true);
    console.log('fetchUserData called with:', { dateFrom, dateTo, userId: selectedUser.id });
    try {
      const isDriver = selectedUser.is_driver || selectedUser.role === 'driver';
      const isMechanic = selectedUser.is_mechanic;

      // Fetch bonuses/penalties
      const bpResponse = await axios.get(
        `${API}/api/bonuses-penalties?user_id=${selectedUser.id}`, 
        getAuthHeaders()
      );
      const filteredBP = bpResponse.data.filter(bp => {
        const bpDate = (bp.date || bp.created_at).split('T')[0];
        if (dateFrom && bpDate < dateFrom) return false;
        if (dateTo && bpDate > dateTo) return false;
        return true;
      });
      setBonusesPenalties(filteredBP);

      // Fetch trips for drivers (including future trips)
      if (isDriver) {
        const tripsResponse = await axios.get(`${API}/api/trips`, getAuthHeaders());
        console.log('All trips count:', tripsResponse.data.length);
        const userTrips = tripsResponse.data.filter(trip => {
          if (trip.driver_id !== selectedUser.id) return false;
          if (dateFrom && trip.date < dateFrom) return false;
          if (dateTo && trip.date > dateTo) return false;
          return true;
        });
        console.log('Filtered trips for user:', userTrips.length, 'dates:', userTrips.map(t => t.date).slice(0, 5));
        userTrips.sort((a, b) => new Date(b.date) - new Date(a.date));
        setTrips(userTrips);
      } else {
        setTrips([]);
      }

      // Fetch mechanic tasks
      if (isMechanic) {
        const tasksResponse = await axios.get(
          `${API}/api/mechanic-tasks?mechanic_id=${selectedUser.id}`,
          getAuthHeaders()
        );
        const filteredTasks = tasksResponse.data.filter(task => {
          const taskDate = (task.completed_at || task.created_at || '').split('T')[0];
          if (dateFrom && taskDate < dateFrom) return false;
          if (dateTo && taskDate > dateTo) return false;
          return true;
        });
        filteredTasks.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setMechanicTasks(filteredTasks);
      } else {
        setMechanicTasks([]);
      }
      
      setRepairs([]);

    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setDataLoading(false);
    }
  };

  const handlePeriodPreset = (preset) => {
    setPeriodPreset(preset);
    const now = new Date();
    
    switch (preset) {
      case 'thisMonth':
        setDateFrom(formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 1)));
        setDateTo(formatDateLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
        break;
      case 'lastMonth':
        setDateFrom(formatDateLocal(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
        setDateTo(formatDateLocal(new Date(now.getFullYear(), now.getMonth(), 0)));
        break;
      case 'last12Months':
        setDateFrom(formatDateLocal(new Date(now.getFullYear() - 1, now.getMonth(), 1)));
        setDateTo(formatDateLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
        break;
      default:
        break;
    }
  };

  const handleCreateBonus = async () => {
    if (!bonusForm.amount || bonusForm.amount <= 0 || !bonusForm.reason) {
      toast.error('Заполните все поля и укажите сумму больше 0');
      return;
    }

    try {
      await axios.post(`${API}/api/bonuses-penalties`, {
        user_id: selectedUser.id,
        user_name: selectedUser.username,
        user_role: 'driver',
        type: bonusForm.type,
        amount: parseFloat(bonusForm.amount),
        reason: bonusForm.reason,
        date: bonusForm.date
      }, getAuthHeaders());
      
      toast.success(`${bonusForm.type === 'bonus' ? 'Премия' : 'Вычет'} добавлен(а)`);
      setShowBonusDialog(false);
      setBonusForm({ type: 'bonus', amount: '', reason: '', date: new Date().toISOString().split('T')[0] });
      fetchUserData();
    } catch (error) {
      console.error('Error creating bonus/penalty:', error);
      toast.error('Ошибка при создании');
    }
  };

  const handleDeleteBonus = async (id) => {
    if (!window.confirm('Удалить эту запись?')) return;

    try {
      await axios.delete(`${API}/api/bonuses-penalties/${id}`, getAuthHeaders());
      toast.success('Запись удалена');
      fetchUserData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Ошибка при удалении');
    }
  };

  const handleDeleteTrip = async (tripId, tripName) => {
    if (!window.confirm(`Удалить рейс "${tripName}"?`)) return;

    try {
      await axios.delete(`${API}/api/trips/${tripId}`, getAuthHeaders());
      toast.success('Рейс удалён');
      fetchUserData();
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error('Ошибка при удалении рейса');
    }
  };

  const getFilteredUsers = () => {
    if (filterRole === 'all') return users;
    if (filterRole === 'driver') return users.filter(u => u.is_driver || u.role === 'driver');
    if (filterRole === 'mechanic') return users.filter(u => u.is_mechanic);
    return users;
  };

  const calculateStats = () => {
    // Фильтруем рейсы - только до сегодня включительно (без будущих)
    const today = new Date().toISOString().split('T')[0];
    const pastTrips = trips.filter(t => t.date <= today);
    
    const bonuses = bonusesPenalties.filter(bp => bp.type === 'bonus').reduce((sum, bp) => sum + bp.amount, 0);
    const penalties = bonusesPenalties.filter(bp => bp.type === 'penalty').reduce((sum, bp) => sum + bp.amount, 0);
    const clientMoney = bonusesPenalties.filter(bp => bp.type === 'client_money').reduce((sum, bp) => sum + bp.amount, 0);
    const tripsSalary = pastTrips.reduce((sum, t) => sum + (t.driver_salary || 0), 0);
    const tripsPaymentRaw = pastTrips.reduce((sum, t) => sum + (t.payment_amount || 0), 0);
    const tripsPayment = tripsPaymentRaw - clientMoney;
    const workDays = new Set(pastTrips.map(t => t.date)).size;

    // Mechanic stats
    const completedMechanicTasks = mechanicTasks.filter(t => t.status === 'completed');
    const dirtyHours = completedMechanicTasks.reduce((sum, t) => sum + (t.dirty_hours || 0), 0);
    const cleanHours = completedMechanicTasks.reduce((sum, t) => sum + (t.clean_hours || 0), 0);
    const dirtyPayment = completedMechanicTasks.reduce((sum, t) => sum + (t.dirty_payment || 0), 0);
    const cleanPayment = completedMechanicTasks.reduce((sum, t) => sum + (t.clean_payment || 0), 0);
    const mechanicTotal = completedMechanicTasks.reduce((sum, t) => sum + (t.total_payment || 0), 0);

    const total = tripsSalary - tripsPayment + bonuses - penalties + mechanicTotal;
    
    return {
      bonuses,
      penalties,
      clientMoney,
      tripsSalary,
      tripsPayment,
      tripsPaymentRaw,
      total,
      workDays,
      tripsCount: pastTrips.length,
      // Mechanic
      dirtyHours: Math.round(dirtyHours * 100) / 100,
      cleanHours: Math.round(cleanHours * 100) / 100,
      dirtyPayment: Math.round(dirtyPayment * 100) / 100,
      cleanPayment: Math.round(cleanPayment * 100) / 100,
      mechanicTotal: Math.round(mechanicTotal * 100) / 100,
      mechanicTasksCount: completedMechanicTasks.length,
      mechanicTasksAll: mechanicTasks.length
    };
  };

  const stats = selectedUser ? calculateStats() : null;
  const isDriver = selectedUser?.is_driver || selectedUser?.role === 'driver';
  const isMechanic = selectedUser?.is_mechanic;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8 bg-gray-50">
      {/* Header */}
      <div className="shadow-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/')}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border border-white/30"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Назад
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
                  <Users className="w-7 h-7" />
                  Отчёты по сотрудникам
                </h1>
                <p className="text-sm text-white/90 mt-1">
                  Информация о рейсах, работах, премиях и вычетах
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('employees')}
            className={`flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'employees'
                ? 'bg-blue-500 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100 border'
            }`}
          >
            <Users className="w-4 h-4" />
            Сотрудники
          </button>
          <button
            onClick={() => setActiveTab('salary')}
            className={`flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'salary'
                ? 'bg-green-500 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100 border'
            }`}
          >
            <Calculator className="w-4 h-4" />
            Зарплаты
          </button>
        </div>

        {activeTab === 'salary' ? (
          <SalaryReport />
        ) : (
        <>
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            {/* Period Selection */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => handlePeriodPreset('thisMonth')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  periodPreset === 'thisMonth'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Текущий месяц
              </button>
              <button
                onClick={() => handlePeriodPreset('lastMonth')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  periodPreset === 'lastMonth'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Прошлый месяц
              </button>
              <button
                onClick={() => handlePeriodPreset('last12Months')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  periodPreset === 'last12Months'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                12 месяцев
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              {/* Role Filter */}
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Роль</Label>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="driver">🚗 Водители</SelectItem>
                    <SelectItem value="mechanic">🔧 Механики</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* User Selection */}
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Сотрудник</Label>
                <Select 
                  value={selectedUser?.id || ''} 
                  onValueChange={(id) => setSelectedUser(users.find(u => u.id === id))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите сотрудника" />
                  </SelectTrigger>
                  <SelectContent>
                    {getFilteredUsers().map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.is_driver ? '🚗' : '🔧'} {user.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date From */}
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">С</Label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPeriodPreset('');
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>

              {/* Date To */}
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">По</Label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPeriodPreset('');
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selected User Info */}
        {selectedUser && (
          <>
            {/* User Header */}
            <Card className="mb-6 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    isMechanic ? 'bg-orange-500' : 'bg-blue-500'
                  }`}>
                    {isMechanic ? <Wrench className="w-8 h-8" /> : <Car className="w-8 h-8" />}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{selectedUser.username}</h2>
                    <p className="text-slate-300">
                      {isMechanic ? '🔧 Механик' : '🚗 Водитель'}
                    </p>
                    {selectedUser.email && (
                      <p className="text-sm text-slate-400">{selectedUser.email}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards - For Drivers */}
            {stats && !dataLoading && isDriver && (
              <Card className="mb-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-green-100 uppercase">Зарплата</p>
                      <p className="text-4xl font-bold">{stats.tripsSalary.toFixed(0)} €</p>
                      <p className="text-sm text-green-200 mt-1">{stats.tripsCount} рейсов</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-green-100 mb-1">Рабочих дней</p>
                      <p className="text-5xl font-bold">{stats.workDays}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-green-100 text-xs flex items-center justify-center gap-1">
                        <BarChart3 className="w-3 h-3" /> Итого
                      </p>
                      <p className={`font-bold ${stats.total >= 0 ? '' : 'text-red-200'}`}>{stats.total.toFixed(0)} €</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-green-100 text-xs flex items-center justify-center gap-1">
                        <Banknote className="w-3 h-3" /> От клиента
                      </p>
                      <p className="font-bold">{stats.tripsPayment.toFixed(0)} €</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-green-100 text-xs flex items-center justify-center gap-1">
                        <Gift className="w-3 h-3" /> Премии
                      </p>
                      <p className="font-bold">+{stats.bonuses.toFixed(0)} €</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-green-100 text-xs flex items-center justify-center gap-1">
                        <TrendingDown className="w-3 h-3" /> Вычеты
                      </p>
                      <p className="font-bold">-{stats.penalties.toFixed(0)} €</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards - For Mechanics */}
            {stats && !dataLoading && isMechanic && (
              <Card className="mb-6 bg-gradient-to-r from-orange-500 to-amber-600 text-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-orange-100 uppercase">Итого к выплате</p>
                      <p className="text-4xl font-bold">{(stats.mechanicTotal + stats.bonuses - stats.penalties).toFixed(0)} €</p>
                      <p className="text-sm text-orange-200 mt-1">{stats.mechanicTasksCount} задач выполнено</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-orange-100 mb-1">Всего задач</p>
                      <p className="text-5xl font-bold">{stats.mechanicTasksAll}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-2 text-sm">
                    <div className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-orange-100 text-xs">Грязная работа</p>
                      <p className="font-bold">{stats.dirtyHours} ч</p>
                      <p className="text-xs text-orange-200">{stats.dirtyPayment.toFixed(0)} €</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-orange-100 text-xs">Чистая работа</p>
                      <p className="font-bold">{stats.cleanHours} ч</p>
                      <p className="text-xs text-orange-200">{stats.cleanPayment.toFixed(0)} €</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-orange-100 text-xs">За работу</p>
                      <p className="font-bold">{stats.mechanicTotal.toFixed(0)} €</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-orange-100 text-xs">Премии</p>
                      <p className="font-bold text-green-300">+{stats.bonuses.toFixed(0)} €</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-orange-100 text-xs">Вычеты</p>
                      <p className="font-bold text-red-300">-{stats.penalties.toFixed(0)} €</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {dataLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Загрузка данных...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Trips for Drivers */}
                {isDriver && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <MapPin className="w-5 h-5 text-blue-500" />
                        Рейсы ({trips.length})
                    </CardTitle>
                    {trips.length > 0 && (
                      <div className="flex items-center gap-4 text-xs mt-2">
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded bg-red-500"></div>
                          <span className="text-gray-600">Сегодня</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded bg-blue-500"></div>
                          <span className="text-gray-600">Будущие</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded bg-gray-200"></div>
                          <span className="text-gray-600">Прошедшие</span>
                        </div>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="max-h-[400px] overflow-auto">
                    {trips.length === 0 && (
                      <p className="text-center text-gray-500 py-8">Нет рейсов за этот период</p>
                    )}
                    
                    {trips.map(trip => {
                      const today = new Date().toISOString().split('T')[0];
                      const isFuture = trip.date > today;
                      const isToday = trip.date === today;
                      const tripDate = new Date(trip.date);
                      const day = tripDate.getDate();
                      const month = tripDate.toLocaleDateString('ru-RU', { month: 'short' });
                      return (
                      <div key={trip.id} className={`p-3 sm:p-4 border-b-2 last:border-0 ${
                        isToday ? 'bg-red-50 border-red-200' : 
                        isFuture ? 'bg-blue-50 border-blue-200' : 
                        'hover:bg-gray-50 border-gray-100'
                      }`}>
                        {/* Mobile Layout */}
                        <div className="sm:hidden">
                          <div className="flex items-start gap-3 mb-2">
                            <div className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl font-bold shrink-0 ${
                              isToday ? 'bg-red-500 text-white' : 
                              isFuture ? 'bg-blue-500 text-white' : 
                              'bg-gray-200 text-gray-700'
                            }`}>
                              <span className="text-xl leading-none">{day}</span>
                              <span className="text-[10px] uppercase">{month}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-gray-800 text-base">{trip.route_name || 'Рейс'}</p>
                                {isToday && (
                                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white">СЕГОДНЯ</span>
                                )}
                                {isFuture && (
                                  <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-500 text-white">БУДУЩИЙ</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-0.5">
                                {trip.start_location?.name || '—'} → {trip.end_location?.name || '—'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mb-2 pl-[68px]">
                            {trip.time_start && (
                              <span className="text-sm font-medium text-gray-600 flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" /> {trip.time_start}
                              </span>
                            )}
                            {trip.bus_number && (
                              <span className="text-sm text-gray-500 flex items-center gap-1">
                                <Car className="w-3.5 h-3.5" /> {trip.bus_number}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center justify-between pl-[68px] pt-2 border-t border-gray-200">
                            <div className="flex items-center gap-4">
                              {trip.payment_amount > 0 && (
                                <div className="text-sm">
                                  <span className="text-gray-500 text-xs block">от клиента</span>
                                  <span className="font-bold text-orange-600">{trip.payment_amount} €</span>
                                </div>
                              )}
                              <div className="text-sm">
                                <span className="text-gray-500 text-xs block">зарплата</span>
                                <span className="font-bold text-green-600 text-lg">{trip.driver_salary || 0} €</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingTrip(trip)}
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTrip(trip.id, trip.route_name || trip.date)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Desktop Layout */}
                        <div className="hidden sm:flex items-center gap-3">
                          <div className={`flex flex-col items-center justify-center min-w-[60px] h-[60px] rounded-xl font-bold ${
                            isToday ? 'bg-red-500 text-white' : 
                            isFuture ? 'bg-blue-500 text-white' : 
                            'bg-gray-200 text-gray-700'
                          }`}>
                            <span className="text-2xl leading-none">{day}</span>
                            <span className="text-xs uppercase">{month}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold text-gray-800">{trip.route_name || 'Рейс'}</p>
                              {isToday && (
                                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white">СЕГОДНЯ</span>
                              )}
                              {isFuture && (
                                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-blue-500 text-white">БУДУЩИЙ</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 truncate">
                              {trip.start_location?.name || '—'} → {trip.end_location?.name || '—'}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {trip.time_start && (
                                <span className="text-sm font-medium text-gray-600">🕐 {trip.time_start}</span>
                              )}
                              {trip.bus_number && (
                                <span className="text-xs text-gray-500">🚌 {trip.bus_number}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 min-w-[80px]">
                            {trip.payment_amount > 0 && (
                              <div className="flex items-center gap-1 text-sm">
                                <span className="text-gray-500 text-xs">от клиента:</span>
                                <span className="font-bold text-orange-600">{trip.payment_amount} €</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <span className="text-gray-500 text-xs">зарплата:</span>
                              <span className="text-lg font-bold text-green-600">{trip.driver_salary || 0} €</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingTrip(trip)}
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTrip(trip.id, trip.route_name || trip.date)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                    })}
                  </CardContent>
                </Card>
                )}

                {/* Mechanic Tasks */}
                {isMechanic && (
                <Card data-testid="mechanic-tasks-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Wrench className="w-5 h-5 text-orange-500" />
                      Задачи механика ({mechanicTasks.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-[400px] overflow-auto">
                    {mechanicTasks.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">Нет задач за этот период</p>
                    ) : (
                      mechanicTasks.map(task => {
                        const taskDate = new Date(task.completed_at || task.created_at);
                        const day = taskDate.getDate();
                        const month = taskDate.toLocaleDateString('ru-RU', { month: 'short' });
                        const isCompleted = task.status === 'completed';
                        return (
                          <div key={task.id} className={`p-3 sm:p-4 border-b-2 last:border-0 ${
                            isCompleted ? 'hover:bg-gray-50 border-gray-100' : 'bg-yellow-50 border-yellow-200'
                          }`}>
                            <div className="flex items-center gap-3">
                              <div className={`flex flex-col items-center justify-center min-w-[50px] h-[50px] rounded-xl font-bold ${
                                isCompleted ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
                              }`}>
                                <span className="text-lg leading-none">{day}</span>
                                <span className="text-[10px] uppercase">{month}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-semibold text-gray-800">
                                    {task.bus_license_plate || task.bus_name || 'Автобус'}
                                  </p>
                                  <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                                    isCompleted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {isCompleted ? 'Выполнено' : 'В работе'}
                                  </span>
                                  {task.is_important && (
                                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700">
                                      Важное
                                    </span>
                                  )}
                                </div>
                                {task.admin_note && (
                                  <p className="text-sm text-gray-500 truncate mt-0.5">{task.admin_note}</p>
                                )}
                                {task.bus_notes && (
                                  <p className="text-xs text-amber-600 truncate mt-0.5">Автобус: {task.bus_notes}</p>
                                )}
                                {task.mechanic_note && (
                                  <p className="text-xs text-gray-400 truncate mt-0.5">Заметка: {task.mechanic_note}</p>
                                )}
                                {isCompleted && (
                                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                    {task.dirty_hours > 0 && <span>Грязная: {task.dirty_hours}ч</span>}
                                    {task.clean_hours > 0 && <span>Чистая: {task.clean_hours}ч</span>}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1 min-w-[70px]">
                                {isCompleted && (
                                  <span className="text-lg font-bold text-green-600">
                                    {(task.total_payment || 0).toFixed(0)} €
                                  </span>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setEditMechTask(task); setShowEditMechDialog(true); }}
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                                  data-testid={`edit-mech-task-${task.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
                )}

                {/* Bonuses & Penalties */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-lg">
                        <Wallet className="w-5 h-5 text-purple-500" />
                        Премии и вычеты ({bonusesPenalties.length})
                      </span>
                      <Button
                        size="sm"
                        onClick={() => setShowBonusDialog(true)}
                        className="bg-purple-500 hover:bg-purple-600"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Добавить
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-[400px] overflow-auto">
                    {bonusesPenalties.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">Нет премий и вычетов за этот период</p>
                    ) : (
                      bonusesPenalties.map(bp => (
                        <div 
                          key={bp.id} 
                          className={`p-3 border-l-4 mb-2 rounded-r-lg ${
                            bp.type === 'bonus' 
                              ? 'border-l-green-500 bg-green-50' 
                              : bp.type === 'client_money'
                              ? 'border-l-blue-500 bg-blue-50'
                              : 'border-l-red-500 bg-red-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium flex items-center gap-1">
                                {bp.type === 'bonus' 
                                  ? <><Gift className="w-4 h-4 text-green-600" /> Премия</>
                                  : bp.type === 'client_money' 
                                  ? <><Banknote className="w-4 h-4 text-blue-600" /> Деньги от клиента</>
                                  : <><TrendingDown className="w-4 h-4 text-red-600" /> Вычет</>
                                }
                              </p>
                              <p className="text-sm text-gray-600">{bp.reason}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(bp.date || bp.created_at).toLocaleDateString('ru-RU')}
                                {bp.created_by_name && ` • ${bp.created_by_name}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <p className={`text-xl font-bold ${
                                bp.type === 'bonus' ? 'text-green-600' : bp.type === 'client_money' ? 'text-blue-600' : 'text-red-600'
                              }`}>
                                {bp.type === 'bonus' ? '+' : '-'}{bp.amount.toFixed(0)} €
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteBonus(bp.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {/* No user selected */}
        {!selectedUser && (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Выберите сотрудника
            </h3>
            <p className="text-gray-500">
              Выберите водителя или механика для просмотра отчёта
            </p>
          </Card>
        )}
        </>
        )}
      </div>

      {/* Add Bonus/Penalty Dialog */}
      <Dialog open={showBonusDialog} onOpenChange={setShowBonusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-600" />
              Добавить для {selectedUser?.username}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Тип</Label>
              <Select 
                value={bonusForm.type} 
                onValueChange={(value) => setBonusForm({...bonusForm, type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonus">💰 Премия</SelectItem>
                  <SelectItem value="penalty">📉 Вычет</SelectItem>
                  <SelectItem value="client_money">💵 Деньги от клиента</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Сумма (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={bonusForm.amount}
                onChange={(e) => setBonusForm({...bonusForm, amount: e.target.value})}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Дата</Label>
              <input
                type="date"
                value={bonusForm.date}
                onChange={(e) => setBonusForm({...bonusForm, date: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              />
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Причина</Label>
              <Textarea
                value={bonusForm.reason}
                onChange={(e) => setBonusForm({...bonusForm, reason: e.target.value})}
                placeholder="Укажите причину..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowBonusDialog(false)} 
                className="flex-1"
              >
                Отмена
              </Button>
              <Button 
                onClick={handleCreateBonus} 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Создать
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Trip Modal */}
      {editingTrip && (
        <EditTripModal
          trip={editingTrip}
          buses={buses}
          drivers={drivers}
          onClose={() => setEditingTrip(null)}
          onSuccess={() => {
            setEditingTrip(null);
            fetchUserData();
          }}
        />
      )}

      {/* Edit Mechanic Task Dialog */}
      <EditMechTaskDialog
        task={editMechTask}
        open={showEditMechDialog}
        onOpenChange={setShowEditMechDialog}
        onSaved={fetchUserData}
      />
    </div>
  );
}


// Edit Trip Modal Component
function EditTripModal({ trip, buses, drivers, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    date: trip.date || '',
    bus_id: trip.bus_id || '',
    driver_id: trip.driver_id || '',
    route_name: trip.route_name || '',
    description: trip.description || '',
    start_location: { 
      name: trip.start_location?.name || '', 
      address: trip.start_location?.address || '', 
      coords: trip.start_location?.lat && trip.start_location?.lng 
        ? `${trip.start_location.lat}, ${trip.start_location.lng}` : '',
      time: trip.time_start || ''
    },
    end_location: { 
      name: trip.end_location?.name || '', 
      address: trip.end_location?.address || '', 
      coords: trip.end_location?.lat && trip.end_location?.lng 
        ? `${trip.end_location.lat}, ${trip.end_location.lng}` : '',
      time: trip.time_end || ''
    },
    contact_name: trip.contact_name || '',
    contact_phone: trip.contact_phone || '',
    contact_email: trip.contact_email || '',
    payment_amount: trip.payment_amount || '',
    expenses: trip.expenses || '',
    driver_salary: trip.driver_salary || ''
  });
  const [waypoints, setWaypoints] = useState(
    trip.stops?.map(wp => ({
      name: wp.name || '',
      address: wp.address || '',
      coords: wp.lat && wp.lng ? `${wp.lat}, ${wp.lng}` : '',
      time: wp.arrival_time || ''
    })) || []
  );
  const [saving, setSaving] = useState(false);

  const parseCoords = (coordsStr) => {
    if (!coordsStr) return { lat: null, lng: null };
    const parts = coordsStr.split(',').map(s => s.trim());
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    return { lat: null, lng: null };
  };

  const updateLocation = (field, key, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: { ...prev[field], [key]: value }
    }));
  };

  const addWaypoint = () => {
    setWaypoints([...waypoints, { name: '', address: '', coords: '', time: '' }]);
  };

  const removeWaypoint = (index) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  };

  const updateWaypoint = (index, field, value) => {
    const updated = [...waypoints];
    updated[index] = { ...updated[index], [field]: value };
    setWaypoints(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.date) {
      toast.error('Выберите дату');
      return;
    }

    setSaving(true);
    try {
      const parsedWaypoints = waypoints
        .filter(wp => wp.name || wp.address || wp.coords || wp.time)
        .map(wp => {
          const { lat, lng } = parseCoords(wp.coords);
          return {
            name: wp.name,
            address: wp.address,
            lat,
            lng,
            arrival_time: wp.time
          };
        });

      const startCoords = parseCoords(formData.start_location.coords);
      const endCoords = parseCoords(formData.end_location.coords);

      const data = {
        ...formData,
        time_start: formData.start_location.time,
        time_end: formData.end_location.time,
        start_location: {
          name: formData.start_location.name,
          address: formData.start_location.address,
          lat: startCoords.lat,
          lng: startCoords.lng
        },
        end_location: {
          name: formData.end_location.name,
          address: formData.end_location.address,
          lat: endCoords.lat,
          lng: endCoords.lng
        },
        stops: parsedWaypoints,
        payment_amount: parseFloat(formData.payment_amount) || 0,
        expenses: parseFloat(formData.expenses) || 0,
        driver_salary: parseFloat(formData.driver_salary) || 0
      };

      await axios.put(`${API}/api/trips/${trip.id}`, data, getAuthHeaders());
      toast.success('Рейс обновлён');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка обновления');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold">Редактирование рейса</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Financial - FIRST for quick access */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
            <Label className="flex items-center gap-2 text-green-700 font-bold mb-2">
              <Wallet className="w-4 h-4" />
              Финансы
            </Label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <span className="text-xs text-slate-600">Оплата €</span>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.payment_amount}
                  onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
              <div>
                <span className="text-xs text-slate-600">Расходы €</span>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.expenses}
                  onChange={(e) => setFormData({ ...formData, expenses: e.target.value })}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
              <div>
                <span className="text-xs text-slate-600">Зарплата €</span>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.driver_salary}
                  onChange={(e) => setFormData({ ...formData, driver_salary: e.target.value })}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Date */}
          <div>
            <Label>Дата *</Label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Route Name */}
          <div>
            <Label>Название маршрута</Label>
            <Input
              value={formData.route_name}
              onChange={(e) => setFormData({ ...formData, route_name: e.target.value })}
              placeholder="Vilnius - Kaunas"
            />
          </div>

          {/* Description */}
          <div>
            <Label>Описание</Label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg resize-none text-sm"
              rows={2}
              placeholder="Дополнительная информация..."
            />
          </div>

          {/* Driver & Bus */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Водитель *</Label>
              <select
                value={formData.driver_id}
                onChange={(e) => setFormData({ ...formData, driver_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">Выберите</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.first_name || d.last_name ? `${d.first_name || ''} ${d.last_name || ''}`.trim() : d.username}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Автобус</Label>
              <select
                value={formData.bus_id}
                onChange={(e) => setFormData({ ...formData, bus_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">Выберите</option>
                {buses.map(b => (
                  <option key={b.id} value={b.id}>{b.number || b.license_plate}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Start Location */}
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            <Label className="flex items-center gap-2">
              <Navigation className="w-4 h-4 text-green-500" />
              Начальная точка *
            </Label>
            <div>
              <span className="text-xs text-slate-500 mb-1 block">Время *</span>
              <TimeInput24h
                value={formData.start_location.time}
                onChange={(val) => updateLocation('start_location', 'time', val)}
              />
            </div>
            <Input
              value={formData.start_location.name}
              onChange={(e) => updateLocation('start_location', 'name', e.target.value)}
              placeholder="Название *"
            />
            <Input
              value={formData.start_location.address}
              onChange={(e) => updateLocation('start_location', 'address', e.target.value)}
              placeholder="Адрес"
            />
            <Input
              value={formData.start_location.coords}
              onChange={(e) => updateLocation('start_location', 'coords', e.target.value)}
              placeholder="Координаты (54.6872, 25.2797)"
            />
          </div>

          {/* Waypoints */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Circle className="w-4 h-4 text-blue-500" />
                Промежуточные точки ({waypoints.length})
              </Label>
              <Button type="button" size="sm" variant="outline" onClick={addWaypoint}>
                <Plus className="w-3 h-3 mr-1" />
                Добавить
              </Button>
            </div>
            
            {waypoints.map((wp, index) => (
              <div key={index} className="bg-blue-50 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-700">Точка {index + 1}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => removeWaypoint(index)}
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <TimeInput24h
                  value={wp.time}
                  onChange={(val) => updateWaypoint(index, 'time', val)}
                />
                <Input
                  value={wp.name}
                  onChange={(e) => updateWaypoint(index, 'name', e.target.value)}
                  placeholder="Название"
                />
                <Input
                  value={wp.address}
                  onChange={(e) => updateWaypoint(index, 'address', e.target.value)}
                  placeholder="Адрес"
                />
                <Input
                  value={wp.coords}
                  onChange={(e) => updateWaypoint(index, 'coords', e.target.value)}
                  placeholder="Координаты"
                />
              </div>
            ))}
          </div>

          {/* End Location */}
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-500" />
              Конечная точка *
            </Label>
            <TimeInput24h
              value={formData.end_location.time}
              onChange={(val) => updateLocation('end_location', 'time', val)}
            />
            <Input
              value={formData.end_location.name}
              onChange={(e) => updateLocation('end_location', 'name', e.target.value)}
              placeholder="Название *"
            />
            <Input
              value={formData.end_location.address}
              onChange={(e) => updateLocation('end_location', 'address', e.target.value)}
              placeholder="Адрес"
            />
            <Input
              value={formData.end_location.coords}
              onChange={(e) => updateLocation('end_location', 'coords', e.target.value)}
              placeholder="Координаты"
            />
          </div>

          {/* Contact */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Контакт</Label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                placeholder="Имя"
              />
            </div>
            <div>
              <Label>Телефон</Label>
              <Input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                placeholder="+370..."
              />
            </div>
            <div>
              <Label>Email</Label>
              <input
                type="text"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                onPaste={(e) => {
                  // Get pasted text directly from clipboard
                  const pastedText = e.clipboardData?.getData('text') || '';
                  if (pastedText) {
                    e.preventDefault();
                    // Get current selection to handle replacement
                    const input = e.target;
                    const start = input.selectionStart || 0;
                    const end = input.selectionEnd || 0;
                    const currentValue = formData.contact_email || '';
                    const newValue = currentValue.substring(0, start) + pastedText + currentValue.substring(end);
                    setFormData({ ...formData, contact_email: newValue });
                    // Set cursor position after paste
                    setTimeout(() => {
                      input.selectionStart = input.selectionEnd = start + pastedText.length;
                    }, 0);
                  }
                }}
                placeholder="email@..."
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Отмена
            </Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// Edit Mechanic Task Dialog
function EditMechTaskDialog({ task, open, onOpenChange, onSaved }) {
  const [adminNote, setAdminNote] = useState('');
  const [mechNote, setMechNote] = useState('');
  const [busNotes, setBusNotes] = useState('');
  const [taskDate, setTaskDate] = useState('');
  const [dStart, setDStart] = useState('');
  const [dEnd, setDEnd] = useState('');
  const [cStart, setCStart] = useState('');
  const [cEnd, setCEnd] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setAdminNote(task.admin_note || '');
      setMechNote(task.mechanic_note || '');
      setBusNotes(task.bus_notes || '');
      setTaskDate((task.completed_at || task.created_at || '').split('T')[0]);
      setDStart(task.dirty_work_start || '');
      setDEnd(task.dirty_work_end || '');
      setCStart(task.clean_work_start || '');
      setCEnd(task.clean_work_end || '');
    }
  }, [task]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        admin_note: adminNote,
        mechanic_note: mechNote,
        bus_notes: busNotes,
        dirty_work_start: dStart || null,
        dirty_work_end: dEnd || null,
        clean_work_start: cStart || null,
        clean_work_end: cEnd || null
      };
      if (taskDate) {
        const origDate = (task.completed_at || task.created_at || '').split('T')[0];
        if (taskDate !== origDate) {
          if (task.completed_at) payload.completed_at = taskDate + 'T' + (task.completed_at.split('T')[1] || '00:00:00');
          payload.created_at = taskDate + 'T' + (task.created_at.split('T')[1] || '00:00:00');
        }
      }
      await axios.put(`${API}/api/mechanic-tasks/${task.id}/edit`, payload, getAuthHeaders());
      toast.success('Работа обновлена');
      onSaved();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-mech-task-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-600" />
            Редактирование работы
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-2.5">
            <Car className="w-4 h-4" />
            <span className="font-semibold">{task.bus_license_plate || task.bus_name}</span>
          </div>

          {/* Date */}
          <div>
            <Label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-gray-500" />
              Дата
            </Label>
            <input
              type="date"
              value={taskDate}
              onChange={(e) => setTaskDate(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>

          {/* Mechanic note */}
          <div>
            <Label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-green-600" />
              Заметка механика
            </Label>
            <Textarea
              data-testid="edit-report-mechanic-note"
              value={mechNote}
              onChange={(e) => setMechNote(e.target.value)}
              placeholder="Заметка механика..."
              rows={2}
            />
          </div>

          {/* Bus notes */}
          <div>
            <Label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-amber-600" />
              Заметки автобуса
            </Label>
            <Textarea
              value={busNotes}
              onChange={(e) => setBusNotes(e.target.value)}
              placeholder="Заметки по автобусу..."
              rows={2}
              className="border-amber-200 bg-amber-50"
            />
          </div>

          <div>
            <Label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
              <User className="w-4 h-4 text-blue-600" />
              Важная работа
            </Label>
            <p className="text-xs text-gray-400 mb-1.5">Если заполнить — работа станет "важной" и попадёт в отчёт</p>
            <Textarea
              data-testid="edit-report-admin-note"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Важная работа..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Время работы:</Label>
            <div className="p-3 rounded-lg border-2 border-orange-200 bg-orange-50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-sm font-bold text-orange-800">Грязная работа</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 w-6">с</span>
                <TimeInput24h value={dStart} onChange={setDStart} />
                <span className="text-xs text-gray-600 w-8 text-center">по</span>
                <TimeInput24h value={dEnd} onChange={setDEnd} />
              </div>
            </div>
            <div className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm font-bold text-blue-800">Чистая работа</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 w-6">с</span>
                <TimeInput24h value={cStart} onChange={setCStart} />
                <span className="text-xs text-gray-600 w-8 text-center">по</span>
                <TimeInput24h value={cEnd} onChange={setCEnd} />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-3 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="edit-report-task-save-btn"
            >
              {saving ? 'Сохранение...' : <><Save className="w-4 h-4 mr-2" />Сохранить</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
